import { z } from 'zod';

/**
 * What the admin backup can export, one worksheet each.
 *
 * `master` is the catalogue and configuration the lab builds up once and edits
 * rarely; it is always exported whole, because half a price list is not a
 * backup of one. `records` is the day-to-day work, and is what the date range
 * narrows. The order here is the order of the sheets in the workbook.
 */
export const BACKUP_ENTITY_GROUPS = {
  master: {
    label: 'Master data',
    entities: {
      users: 'Staff',
      lab_profile: 'Company details',
      departments: 'Departments',
      consultants: 'Consultants',
      panels: 'Panels',
      lab_tests: 'Lab tests',
      test_parameters: 'Test parameters',
      reference_ranges: 'Reference ranges',
      result_options: 'Result options',
      report_comments: 'Report comments',
      report_remarks: 'Report remarks',
      print_templates: 'Print templates',
      test_aliases: 'Lab test aliases',
      test_procedures: 'Test procedures',
      suppliers: 'Suppliers',
      reagent_items: 'Reagent items',
      reagent_recipes: 'Reagent recipes',
      expense_categories: 'Expense categories',
    },
  },
  records: {
    label: 'Records',
    entities: {
      medical_records: 'Medical records',
      patients: 'Patients',
      payments: 'Payments',
      reports: 'Reports',
      report_results: 'Report results',
      day_closes: 'Day closes',
      whatsapp_shares: 'WhatsApp shares',
      reagent_lots: 'Reagent lots',
      stock_movements: 'Stock movements',
      requisitions: 'Purchase requisitions',
      requisition_lines: 'Requisition lines',
      receipts: 'Purchase receipts',
      receipt_lines: 'Receipt lines',
      order_requests: 'Order requests',
      order_request_lines: 'Order request lines',
      expenses: 'Expenses',
    },
  },
} as const;

type Groups = typeof BACKUP_ENTITY_GROUPS;
export type BackupGroup = keyof Groups;
export type BackupEntity = { [G in BackupGroup]: keyof Groups[G]['entities'] }[BackupGroup];

export const BACKUP_GROUPS = Object.keys(BACKUP_ENTITY_GROUPS) as BackupGroup[];

export const backupEntitiesOf = (group: BackupGroup): BackupEntity[] =>
  Object.keys(BACKUP_ENTITY_GROUPS[group].entities) as BackupEntity[];

/** Every entity, in sheet order. */
export const BACKUP_ENTITIES: readonly BackupEntity[] = BACKUP_GROUPS.flatMap(backupEntitiesOf);

export const BACKUP_ENTITY_LABELS = Object.fromEntries(
  BACKUP_GROUPS.flatMap((group) => Object.entries(BACKUP_ENTITY_GROUPS[group].entities)),
) as Record<BackupEntity, string>;

const backupEntitySchema = z.enum(BACKUP_ENTITIES as [BackupEntity, ...BackupEntity[]]);

/**
 * `entities` arrives as one comma-separated query parameter, because the
 * download is a plain link and a link has no body.
 */
export const backupExportQuerySchema = z
  .object({
    entities: z
      .string()
      .transform((value) => [
        ...new Set(
          value
            .split(',')
            .map((entity) => entity.trim())
            .filter(Boolean),
        ),
      ])
      .pipe(z.array(backupEntitySchema).min(1, 'Choose at least one thing to export')),
    from: z.iso.date().optional(),
    to: z.iso.date().optional(),
  })
  .refine((query) => !query.from || !query.to || query.from <= query.to, {
    message: 'The range ends before it starts',
    path: ['to'],
  });
export type BackupExportQuery = z.infer<typeof backupExportQuerySchema>;
